// Implements: F-UIX-018, F-CNV-003
/**
 * The 80 % notice of one mockup's conversation (UIX.1, CNV.2).
 *
 * It reads the person's usage status on opening the mockup and after each turn settles
 * (`useUsageStatus`), and shows one notice only for `warning` naming the person's, the
 * team's or the organization's limit. `ok` (also what the server answers while the
 * enforcement switch is off), `exhausted` (the refusal of the next send says it, S12a-2),
 * an unreadable status and a warning that names no such limit show nothing.
 *
 * The page mounts it keyed by mockup id (checklist 14); it never blocks sending.
 */

import type { ReactElement } from "react";

import type { UsageReason } from "../../../types/api";
import { UsageNotice } from "../components/UsageNotice";
import type { WarnedLimit } from "../components/UsageNotice";
import { useUsageStatus } from "../hooks/useUsageStatus";

const WARNED: readonly UsageReason[] = ["person", "team", "organization"] satisfies WarnedLimit[];

function isWarnedLimit(reason: UsageReason | null): reason is WarnedLimit {
  return reason !== null && WARNED.includes(reason);
}

export interface UsageNoticeContainerProps {
  readonly mockupId: string;
  /** Whether a turn is open on the mockup now; its fall to `false` reads the status again. */
  readonly turnOpen: boolean;
}

export function UsageNoticeContainer({ mockupId, turnOpen }: UsageNoticeContainerProps): ReactElement | null {
  const status = useUsageStatus(mockupId, turnOpen);
  if (status?.state !== "warning" || !isWarnedLimit(status.reason)) return null;
  return <UsageNotice limit={status.reason} />;
}
